import React, { useState } from "react";
import NavbarVisitor from '../Component/NavbarVisitor';
import axios from "axios";
import { BASE_URL } from '../Component/Baseurl';
import Footer from "../Component/Footer";
import Swal from 'sweetalert2'; // Import SweetAlert2
import ProfileNavbar from "../Component/ProfileNavbar";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // Import Font Awesome for eye icon
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'; // Import eye and eye-slash icons

const ChangePassword = () => {
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [isEdited, setIsEdited] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // State to toggle confirm password visibility
  const [passwordValid, setPasswordValid] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Update the respective field value
    setFormData((prevState) => {
      const updatedData = {
        ...prevState,
        [name]: value,
      };

      // Password validation regex for the criteria
      const passwordCriteria = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;

      // Set password validity conditions
      const isPasswordValid = passwordCriteria.test(updatedData.password);

      // Update password validity state
      setPasswordValid({
        length: updatedData.password.length >= 8,
        uppercase: /[A-Z]/.test(updatedData.password),
        lowercase: /[a-z]/.test(updatedData.password),
        number: /\d/.test(updatedData.password),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(updatedData.password),
      });

      // Check if both passwords match and the password is valid
      const isPasswordMatch =
        updatedData.password === updatedData.confirmPassword && updatedData.password !== "" && updatedData.confirmPassword !== "";
      
      // Set isEdited to true if passwords match and password is valid
      setIsEdited(isPasswordMatch && isPasswordValid);

      return updatedData;
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if the passwords match before submitting
    if (formData.password !== formData.confirmPassword) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Passwords do not match!',
      });
      return;
    }

    // Check if the password meets the required criteria
    const passwordCriteria = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
    if (!passwordCriteria.test(formData.password)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Password!',
        text: 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.',
      });
      return;
    }

    // Get the visitorId from localStorage
    const visitorId = localStorage.getItem('visitor_id');

    // Check if the visitorId exists
    if (!visitorId) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Visitor ID not found!',
      });
      return;
    }

    // Prepare the data to send in the API request
    const data = {
      password: formData.password,
      id: visitorId, // Send visitorId along with the password
    };

    // Make the API call to update the password
    try {
      const response = await axios.post(`${BASE_URL}/updatePassword`, data);

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Password Changed!',
          text: 'Your password has been updated successfully.',
        });
        // Clear form fields after successful password change
        setFormData({
          password: "",
          confirmPassword: "",
        });
        setIsEdited(false);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'There was an issue updating your password. Please try again.',
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Something went wrong. Please try again later.',
      });
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  // Utility function to check if the password requirement is met
  const getPasswordValidityClass = (isValid) => isValid ? 'text-success' : 'text-danger';

  return (
    <>
      <NavbarVisitor />
      <section>
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-3">
              <ProfileNavbar />
            </div>
            <div className="col-md-9">
              <div className="bg-salt mt-3 p-2 py-3 mb-3 text-start">
                <h2 className="text-start hedng">Change Password</h2>

                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-8">
                      <div className="mb-2 position-relative">
                        <label className="form-label">New Password</label>
                        <input
                          type={showPassword ? "text" : "password"}
                          className="forminput"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          className="show-password-btn"
                          onClick={togglePasswordVisibility}
                          style={{ position: "absolute", right: "2px", top: "50%" }}
                        >
                          <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                        </button>
                      </div>
                    </div>

                    <div className="col-md-8">
                      <div className="mb-2 position-relative">
                        <label className="form-label">Confirm New Password</label>
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className="forminput"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Confirm your new password"
                        />
                        <button
                          type="button"
                          className="show-password-btn"
                          onClick={toggleConfirmPasswordVisibility}
                          style={{ position: "absolute", right: "2px", top: "50%" }}
                        >
                          <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                        </button>
                      </div>
                    </div>

                    {/* Password criteria with dynamic styling */}
                    <div className="col-md-8">
                      <small className="form-text text-muted">
                        Password must be <br/>
                        <span className={getPasswordValidityClass(passwordValid.length)}>1. 8 characters long</span> <br/>
                        <span className={getPasswordValidityClass(passwordValid.uppercase)}>2. 1 uppercase letter</span> <br/>
                        <span className={getPasswordValidityClass(passwordValid.lowercase)}>3. 1 lowercase letter</span> <br/>
                        <span className={getPasswordValidityClass(passwordValid.number)}>4. 1 number</span> <br/>
                        <span className={getPasswordValidityClass(passwordValid.special)}>5. 1 special character</span>
                      </small>
                    </div>

                    <div className="col-md-12 text-end">
                      <button
                        type="submit"
                        className={`btnn bg-gradient-primary w-25 text-white ${!isEdited ? 'disablbtn' : ''}`}
                        disabled={!isEdited}
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default ChangePassword;
